import React, { useMemo, useState } from 'react';
import { IconButton, Stack, Typography } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { useDataContext } from '../Context/useDataContext';
import { useLSContext } from "../Context/useLSContext";
import { useHoleSettings } from "../Context/useHoleSettingsContext";
import { ToolsDrawer } from "./ToolsDrawer";
import { InfoDrawer } from "./InfoDrawer";
import NumPad from "./NumPad";
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import lsLogo from '../Assets/ls-button-cropped.png'

interface FloatingActionBarProps {
    preHoleRefresh: () => Promise<void>;
}

export const FloatingActionBar = ({ preHoleRefresh }: FloatingActionBarProps) => {

    const navigate = useNavigate();

    const { setToggleScoring, scoreLock, endOfRoundPopupOpen, setToolsOpen } = useLSContext();
    const { eventData, scoreCode, currentRound, registrationData, groupScorer } = useDataContext();
    const { holeSettings } = useHoleSettings();

    const holeHasChallenge = useMemo(() => {
        const activeRound = eventData?.rounds?.find((r: any) => r.roundNumber === currentRound);
        return activeRound?.challenges?.some((c: any) => c.hole === holeSettings.hole) ?? false;
    }, [eventData, currentRound, holeSettings.hole]);

    const reducedRegistrations = useMemo(() => {
        const deduplicated = registrationData?.reduce((acc: any, reg: any) => {
            if (!acc.some((item: any) => item.scoreCode === reg.scoreCode)) acc.push(reg);
            return acc;
        }, []) || [];
        return deduplicated.sort((a: any, b: any) => {
            if (a.scoreCode === scoreCode) return -1;
            if (b.scoreCode === scoreCode) return 1;
            return 0;
        });
    }, [registrationData, scoreCode]);

    const [infoOpen, setInfoOpen] = useState(false);

    const allowChangeHoles = window.location.href.includes('live-scoring');
    const allowActionBar = window.location.href.includes('live-scoring') || window.location.href.includes('leaderboard');

    const showNumPad = (groupScorer || eventData?.scoreType?.toLowerCase() === 'opponent')
        && allowChangeHoles && !scoreLock && !endOfRoundPopupOpen;

    const highScore = eventData?.maxHoleScoreInfo?.overPar === 'par'
        ? holeSettings.par * 2
        : eventData?.maxHoleScoreInfo?.overPar
            ? holeSettings.par + eventData.maxHoleScoreInfo.overPar
            : 0;

    return (
        <Stack direction='column' spacing={1} width='100%' height='max-content' alignItems='center'
        sx={{ position: 'absolute',
            bottom: `calc(env(safe-area-inset-bottom) + 16px)`,
            right: 0,
            zIndex: 1000,
             }}>
        {showNumPad && (
            <NumPad
                highScore={highScore}
                lowScore={1}
                reducedRegistrations={reducedRegistrations}
            />
        )}
        <Stack id='floating-action-bar' direction='row' alignItems='center' justifyContent='flex-start' spacing={0}
            sx={{
                width: '88%',
                maxWidth: '420px',
                height: 50,
                boxShadow: '0 12px 48px rgba(0, 0, 0, 0.4)',
                padding: '4px 12px',
                borderRadius: '999px',
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.55)',
                zIndex: 1000,
            }}
        >
            
            
            <Stack direction='column' flex={1} justifyContent='center' alignItems='center'>
                <IconButton
                    disabled={!allowActionBar}
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 15,
                        '&:hover': {
                            backgroundColor: 'transparent',
                        }
                    }}
                    onClick={() => setInfoOpen(true)}
                >
                    <InfoOutlinedIcon sx={{ color: 'primary.main', fontSize: 33 }} />
                </IconButton>
                <Typography variant='subtitle2' sx={{ width: '100%', color: 'primary.main', fontSize: 12, textAlign: 'center' }}>Info</Typography>
            </Stack>
            <Stack direction='column' flex={1} justifyContent='center' alignItems='center'
                sx={{
                    borderRadius: 2,
                    ...(holeHasChallenge && !scoreLock && allowChangeHoles && {
                        boxShadow: '0 0 10px 3px rgba(184, 115, 51, 0.55)',
                        backgroundColor: 'rgba(184, 115, 51, 0.08)',
                    }),
                }}
            >
                <IconButton
                    disabled={!allowChangeHoles || !allowActionBar || scoreLock}
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 15,
                        '&:hover': {
                            backgroundColor: 'transparent',
                        }
                    }}
                    onClick={() => setToolsOpen(true)}
                >
                    <BuildCircleOutlinedIcon sx={{
                        color: (!allowChangeHoles || !allowActionBar || scoreLock) ? 'grey'
                            : holeHasChallenge ? 'rgb(184, 115, 51)' : 'primary.main',
                        fontSize: 32
                    }} />
                </IconButton>
                <Typography variant='subtitle2' sx={{
                    width: '100%',
                    color: (!allowChangeHoles || !allowActionBar || scoreLock) ? 'grey'
                        : holeHasChallenge ? 'rgb(184, 115, 51)' : 'primary.main',
                    fontSize: 12,
                    textAlign: 'center'
                }}>Tools</Typography>
            </Stack>
            <Stack direction='column' flex={1} justifyContent='center' alignItems='center'>
                <IconButton
                    disabled={!allowActionBar}
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 15,
                        //backgroundColor: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'transparent',
                        }
                    }}
                    onClick={() => {
                        setToggleScoring(false)
                        navigate(`/live-scoring/?eventID=${eventData._id}&scoreCode=${scoreCode}`)
                    }}
                >
                    <img src={lsLogo} alt="Live Scoring" style={{ width: 30, height: 30 }} />
                </IconButton>
                <Typography variant='subtitle2' sx={{ width: '100%', color: 'primary.main', fontSize: 12, textAlign: 'center' }}>Live Scoring</Typography>
            </Stack>
            <Stack direction='column' flex={1} justifyContent='center' alignItems='center'>
                <IconButton
                    disabled={!allowActionBar}
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 15,
                        //backgroundColor: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'transparent',
                        }
                    }}
                    onClick={() => {
                        setToggleScoring(false)
                        navigate(`/leaderboard/?eventID=${eventData._id}`)
                    }}
                >
                    <FormatListNumberedIcon sx={{ color: 'primary.main', fontSize: 35 }} />
                </IconButton>
                <Typography variant='subtitle2' sx={{ width: '100%', color: 'primary.main', fontSize: 12, textAlign: 'center' }}>Leaderboard</Typography>
            </Stack>
            <ToolsDrawer />
            <InfoDrawer open={infoOpen} setOpen={setInfoOpen} />
        </Stack>
        </Stack>
    )
}